import { Association, User } from "@entities";
import { associationRepo, userRepo } from "@repositories";
import { useQuery } from "@tanstack/react-query";
import { normalizePhoneDigitsForSearch } from "@shared/helpers/normalize-phone-for-search";
import { useDebounce } from "../../../react-table/data-table/use-debounce";

const AID_RELATED_SEARCH_STALE_MS = 1000 * 60 * 60;

type Params = {
  organizationId: string | undefined;
  phoneNumber: string | undefined;
  name: string | undefined;
};

export function useAidRelatedUserSearch({
  organizationId,
  phoneNumber,
  name,
}: Params) {
  const debouncedPhone = useDebounce(phoneNumber, 600);
  const debouncedName = useDebounce(name, 600);
  const phoneSearchDigits = normalizePhoneDigitsForSearch(debouncedPhone ?? "");

  const phoneQuery = useQuery({
    queryKey: [
      "aid-related-users-by-phone",
      organizationId,
      phoneSearchDigits,
    ],
    queryFn: () =>
      userRepo.find({
        where: {
          $and: [
            User.isInOrganization({
              organizationId: organizationId || "",
            }),
            { phoneNumber: { $contains: phoneSearchDigits } },
          ],
        },
      }),
    staleTime: AID_RELATED_SEARCH_STALE_MS,
    enabled:
      !!organizationId &&
      phoneSearchDigits.length > 2,
  });

  const nameQuery = useQuery({
    queryKey: ["aid-related-users-by-name", organizationId, debouncedName],
    queryFn: () =>
      associationRepo.find({
        where: Association.hyperFilter({
          organizationId: organizationId || "",
          user: {
            $is: {
              name: { $contains: debouncedName },
            },
          },
        }),
        include: { user: true },
      }),
    staleTime: AID_RELATED_SEARCH_STALE_MS,
    enabled:
      !!organizationId &&
      !!debouncedName &&
      debouncedName.trim().length >= 2,
  });

  return {
    usersByPhone: phoneQuery.data ?? [],
    associationsByName: nameQuery.data ?? [],
    isPhoneLoading: phoneQuery.isLoading,
    isNameLoading: nameQuery.isLoading,
  };
}
